/* eslint-disable no-console */
import moment from 'moment';

import instance from '../xhrService';
import { withOfflineFallback } from '../offlineFallback';

const baseUrl = '/api/weather';
const getBaseUrl = (a, b) => `${baseUrl}/${a}/${b}`;
const localStorageKey = 'weather';

const weatherApi = {
  fetch: (latitude, longitude) =>
    // SW2-2321: was a `navigator.onLine` pre-check whose offline branch simply
    // rejected with "error" — even though the online path had already stored the
    // reading in localStorage. That is the recurring pattern from
    // docs/architecture/offline.md: the data is cached and the read path ignores
    // it. Now serves the stored reading, reusing index()'s staleness rule so a
    // previous day's weather is never presented as today's.
    withOfflineFallback(
      () =>
        instance
          .get(getBaseUrl(latitude, longitude))
          .then(({ data }) => {
            if (data && data.name === 'Error') {
              weatherApi.store([]);
            } else {
              weatherApi.store(data);
            }

            return data;
          })
          .catch((err) => {
            console.log('err getting weather', err);

            // Deliberately no `weatherApi.clear()` here and no `return 'error'`.
            //
            // Returning a value *resolved* the promise, so withOfflineFallback
            // would never see a failure and the cache below could never be
            // reached — the conversion would be inert. It also meant the caller
            // in WeatherContext read `'error'.name`, got undefined, decided
            // there was no error and set the string itself as the weather.
            //
            // Clearing was worse: it wiped the stored reading that the offline
            // fallback exists to serve. WeatherContext already has its own
            // .catch for a genuine failure, so rethrowing is what it expects.
            throw err;
          }),
      () => {
        const stored = weatherApi.index();

        // index() returns [] for missing, corrupt or stale (not-today) data.
        // Returning null lets withOfflineFallback rethrow rather than presenting
        // an empty reading as a successful fetch.
        return Promise.resolve(
          Array.isArray(stored) && stored.length === 0 ? null : stored,
        );
      },
    ),

  index: () => {
    const weatherData = localStorage.getItem(localStorageKey);

    if (!weatherData) return [];

    // wrap in try-catch in case localStorage value gets corrupted
    try {
      const today = moment().format(moment.HTML5_FMT.DATE);
      const weatherDate = localStorage.getItem('weather date');

      if (today === weatherDate) {
        return JSON.parse(weatherData);
      }

      return [];
    } catch (error) {
      weatherApi.clear();

      return [];
    }
  },

  store: (data) => {
    localStorage.setItem(
      'weather date',
      moment().format(moment.HTML5_FMT.DATE),
    );
    localStorage.setItem(localStorageKey, JSON.stringify(data));
  },

  clear: () => {
    localStorage.removeItem(localStorageKey);
    localStorage.removeItem('weather date');
  },

  fetchByProjectId: (latitude, longitude, projectId) => {
    return instance
      .get(getBaseUrl(latitude, longitude))
      .then(({ data }) => {
        if (data && data.name === 'Error') {
          weatherApi.storeByProjectId([], projectId);
        } else {
          weatherApi.storeByProjectId(data, projectId);
        }

        return data;
      })
      .catch((err) => {
        console.log('err getting weather', err);
        weatherApi.clearByProjectId(projectId);

        return 'error';
      });
  },

  indexByProjectId: (projectId) => {
    const weatherData = localStorage.getItem(localStorageKey + projectId);

    if (!weatherData) return [];

    // wrap in try-catch in case localStorage value gets corrupted
    try {
      const today = moment().format(moment.HTML5_FMT.DATE);
      const weatherDate = localStorage.getItem('weather date' + projectId);

      if (today === weatherDate) {
        return JSON.parse(weatherData);
      }

      return [];
    } catch (error) {
      weatherApi.clearByProjectId(projectId);

      return [];
    }
  },

  storeByProjectId: (data, projectId) => {
    localStorage.setItem(
      'weather date' + projectId,
      moment().format(moment.HTML5_FMT.DATE),
    );
    localStorage.setItem(localStorageKey + projectId, JSON.stringify(data));
  },

  clearByProjectId: (projectId) => {
    localStorage.removeItem(localStorageKey + projectId);
    localStorage.removeItem('weather date' + projectId);
  },
};

export default weatherApi;
